"use client";

import { useEffect, useRef } from "react";

type Props = {
  html: string;
  className: string;
  dataCmsKey?: string;
};

export function CmsStaticPage({
  html,
  className,
  dataCmsKey,
}: Readonly<Props>) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // html originates from the internal BFF/CMS API (server-controlled).
    // Sanitization is intentionally omitted: DOMPurify strips <script> tags,
    // but sequential script execution is the whole point of this component
    // (wallet widget requires React 18 UMD → styled-components → Wallet.render).
    el.innerHTML = html;

    // Scripts inside innerHTML strings are inert — they don't execute.
    // Replace each inert <script> with a live element, running them sequentially
    // so dependency order is preserved (e.g. wallet.min.js loads before Wallet.render runs).
    const scripts = Array.from(el.querySelectorAll("script"));
    let active = true;

    function next(i: number): void {
      if (!active || i >= scripts.length) return;
      const old = scripts[i];
      const live = document.createElement("script");
      if (old.src) {
        live.src = old.src;
        live.onload = () => next(i + 1);
        live.onerror = () => next(i + 1);
      } else {
        live.textContent = old.textContent;
      }
      old.replaceWith(live);
      if (!old.src) next(i + 1);
    }

    next(0);

    // Prevent onload/onerror callbacks from firing after unmount.
    // Scripts already inserted into the DOM will still load, but the
    // chain stops — no further script elements are created.
    return () => {
      active = false;
    };
  }, [html]);

  // dangerouslySetInnerHTML makes React treat this div as opaque — it skips
  // child reconciliation during hydration entirely. suppressHydrationWarning
  // silences any attribute/content mismatch (e.g. #dafiti-seo-block appended
  // by the after_content DOMContentLoaded script before React hydrates).
  // React never updates innerHTML on re-renders because __html is always "".
  // useEffect then overwrites with the real HTML after hydration.
  return (
    <div
      ref={ref}
      className={className}
      data-cms-key={dataCmsKey}
      dangerouslySetInnerHTML={{ __html: "" }}
      suppressHydrationWarning
    />
  );
}
